// src/components/PrayerPresentation.js
import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { PRAYER_CONTENT } from '../data/prayerContent';

const PrayerPresentation = ({ factorNumber, onClose, initialSlide = 0 }) => {
  const [currentSlide, setCurrentSlide] = useState(initialSlide);
  const factor = PRAYER_CONTENT[factorNumber];

  // Total slides: 1 title slide + 5 prayer points
  const totalSlides = 6;

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'ArrowRight') {
        nextSlide();
      } else if (e.key === 'ArrowLeft') {
        prevSlide();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentSlide]);

  const nextSlide = () => {
    if (currentSlide < totalSlides - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const renderTitleSlide = () => (
    <div className="h-full flex flex-col items-center justify-center p-4 sm:p-6 md:p-8 lg:p-12 text-center">
      <div className="mb-4 sm:mb-6 md:mb-8">
        <span className="text-4xl sm:text-5xl md:text-6xl">🙏</span>
      </div>
      <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 md:mb-8 leading-tight px-2">
        PRAYER FOCUS
      </h1>
      <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold mb-6 sm:mb-8 md:mb-12 text-yellow-300 px-2">
        {factor.factorName}
      </h2>
      <div className="max-w-4xl px-4">
        <p className="text-base sm:text-lg md:text-xl lg:text-2xl italic leading-relaxed opacity-90">
          {factor.foundationScripture}
        </p>
      </div>
      <div className="mt-8 sm:mt-12 md:mt-16 text-sm sm:text-base md:text-lg lg:text-xl opacity-75">
        5 Prayer Points to Guide Your Hearts
      </div>
    </div>
  );

  const renderPrayerSlide = (prayerPoint) => (
    <div className="h-full flex flex-col items-center justify-center p-4 sm:p-6 md:p-8 lg:p-12 text-center">
      <div className="mb-4 sm:mb-6">
        <div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-full bg-white/20 text-2xl sm:text-3xl font-bold">
          {prayerPoint.number}
        </div>
      </div>
      
      <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-6 sm:mb-8 md:mb-12 leading-tight max-w-5xl px-2">
        {prayerPoint.title}
      </h2>
      
      <div className="max-w-4xl mb-6 sm:mb-8 md:mb-12 bg-white/10 rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 lg:p-10 mx-4">
        <p className="text-base sm:text-lg md:text-2xl lg:text-3xl italic leading-relaxed">
          "{prayerPoint.prayer}"
        </p>
      </div>
      
      <div className="text-sm sm:text-base md:text-lg lg:text-xl opacity-90 px-4">
        <span className="font-semibold">Scripture: </span>
        {prayerPoint.scripture}
      </div>
    </div>
  );

  const renderSlide = () => {
    if (currentSlide === 0) {
      return renderTitleSlide();
    } else {
      const prayerIndex = currentSlide - 1;
      return renderPrayerSlide(factor.points[prayerIndex]);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-gradient-to-br from-purple-900 via-pink-900 to-purple-800 text-white">
      {/* Main Content - Add padding bottom for control bar */}
      <div className="h-full w-full overflow-y-auto pb-16 sm:pb-20 md:pb-24">
        {renderSlide()}
      </div>

      {/* Control Bar - Responsive */}
      <div className="fixed bottom-0 left-0 right-0 bg-black/40 backdrop-blur-md flex items-center justify-between px-2 sm:px-4 md:px-8 py-2 sm:py-3 md:py-4">
        {/* Left: Navigation */}
        <div className="flex items-center gap-1 sm:gap-2 md:gap-4">
          <button
            onClick={prevSlide}
            disabled={currentSlide === 0}
            className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 rounded-lg transition-all text-xs sm:text-sm md:text-base ${
              currentSlide === 0
                ? 'bg-white/10 text-white/30 cursor-not-allowed'
                : 'bg-white/20 hover:bg-white/30 text-white'
            }`}
          >
            <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="hidden sm:inline font-medium">Previous</span>
            <span className="sm:hidden font-medium">Prev</span>
          </button>
          
          <button
            onClick={nextSlide}
            disabled={currentSlide === totalSlides - 1}
            className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 rounded-lg transition-all text-xs sm:text-sm md:text-base ${
              currentSlide === totalSlides - 1
                ? 'bg-white/10 text-white/30 cursor-not-allowed'
                : 'bg-white/20 hover:bg-white/30 text-white'
            }`}
          >
            <span className="font-medium">Next</span>
            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
          
          <div className="ml-1 sm:ml-2 md:ml-4 text-sm sm:text-base md:text-lg font-medium">
            {currentSlide + 1}/{totalSlides}
          </div>
        </div>

        {/* Center: Mode Indicator - Hidden on very small screens */}
        <div className="hidden md:flex items-center gap-3">
          <span className="text-2xl">🙏</span>
          <div className="text-center">
            <div className="text-sm opacity-75">Prayer Mode</div>
            <div className="text-lg font-semibold">Factor {factorNumber}</div>
          </div>
        </div>

        {/* Right: Exit Button */}
        <button
          onClick={onClose}
          className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 rounded-lg bg-red-600 hover:bg-red-700 text-white transition-all text-xs sm:text-sm md:text-base"
        >
          <X className="w-4 h-4 sm:w-5 sm:h-5" />
          <span className="hidden sm:inline font-medium">Exit</span>
        </button>
      </div>

      {/* Keyboard Shortcuts Hint - Hidden on mobile, shown on desktop */}
      <div className="hidden lg:block fixed top-4 right-4 bg-black/40 backdrop-blur-md rounded-lg px-4 py-2 text-sm opacity-75">
        <div className="flex items-center gap-4">
          <span>← → Navigate</span>
          <span>ESC Exit</span>
        </div>
      </div>
    </div>
  );
};

export default PrayerPresentation;